/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import{ useEffect, useState } from "react";
import { API, CONFIGUREROUTES } from "../../../globals/globals";
import { useGetZoneCellsQuery, useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { lteZonesColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { setConfigCounts, setZoneCellIndexes, setZoneDeviceList } from "../../../store/api/deviceConfig/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { IFullDeviceDetails, IZoneDevice } from "../../../store/api/deviceConfig/types";
import { selectedZoneSelector, zoneDeviceListSelector } from "../../../store/api/deviceConfig/selectors";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { OPERATIONTYPES } from "../../../globals/constants";
import { prepareZoneDevicePayload1 } from "../../../components/Configuration/helpers";
import { setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useZoneCellsData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  let zoneDeviceList: IZoneDevice[] = useSelector(zoneDeviceListSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [NoOfSelectedRow, setNoOfSelectedRow] = useState(0);
  const [selectedRow, setSelectedRow] = useState<IZoneDevice[]>([]);
  const [selectedRowIDs, setSelectedRowIDs] = useState<any>([]);
  const [action, setAction] = useState("enable911");

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const { zone = initialState.pagination } = useSelector(paginationModelSelector);
  const selectedZoneData = useSelector(selectedZoneSelector);


  useEffect(() => {
    dispatch(setIsLoadingStatus({ [action]: configStatus.isLoading }));
  }, [action, configStatus.isLoading, dispatch]);

  const args = {
    filters: `page=${zone.page.toString()}&size=${zone.pageSize.toString()}`,
    deviceKey: locationPath[2],
    deviceNode: deviceDetail?.nodeType,
  };
  const { data, isLoading,isFetching, refetch } = useGetZoneCellsQuery(args, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (data?.totalItems >= 0) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.ZONES.Path]: data?.totalItems || 0 }));
    }
  }, [data?.totalItems, dispatch]);

  useEffect(() => {
    if (!!data?.data && data?.data.length > 0) {
      dispatch(setZoneDeviceList(data?.data));
      dispatch(setZoneCellIndexes(data?.additionalData as string[]));
    } else {
      dispatch(setZoneDeviceList([]));
      dispatch(setZoneCellIndexes([]));
    }
  }, [data?.data, dispatch]);

  const zonesColumns = lteZonesColumns(deviceDetail?.nodeType || "");

  const handleEnableBtn = async () => {
    if (selectedRow?.length > 0) {
      setAction("enable911");
      try {
        let response: any;
        const operationType = OPERATIONTYPES.UPDATE;
        const lanDevicePayload =
          !!zoneDeviceList &&
          !!deviceDetail?.objectKey &&
          prepareZoneDevicePayload1(selectedRow, selectedRowIDs, deviceDetail?.objectKey, deviceDetail?.nodeType, operationType);
        response = await setDeviceConfig(lanDevicePayload);
        if (response?.error || configStatus?.isError) throw response?.error;
        if (response.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data update request has submitted`,
            })
          );
          refetch();
        }
        setSelectedRowIDs([]);
        setNoOfSelectedRow(0);
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        setSelectedRowIDs([]);
        setNoOfSelectedRow(0);
      }
    }
    //handle prepareZonePayload for UPDATE
  };

  const handleDeleteZone = async () => {
    if (selectedRow?.length === 1) {
      setAction("deleteZone");
      try {
        let response: any;
        const lanDevicePayload =
          !!zoneDeviceList &&
          !!deviceDetail?.objectKey &&
          prepareZoneDevicePayload1(selectedRow, selectedRowIDs, deviceDetail?.objectKey, deviceDetail?.nodeType, OPERATIONTYPES.DELETE);
        response = await setDeviceConfig(lanDevicePayload);
        if (response?.error || configStatus?.isError) throw response?.error;
        if (response.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The delete request has been initiated`,
            })
          );
          refetch();
        }
        setSelectedRowIDs([]);
        setNoOfSelectedRow(0);
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        setSelectedRowIDs([]);
        setNoOfSelectedRow(0);
      }
    }
  };

  return {
    permissions,
    data,
    isLoading,
    zonesColumns,
    NoOfSelectedRow,
    setNoOfSelectedRow,
    selectedRow,
    setSelectedRow,
    selectedRowIDs,
    setSelectedRowIDs,
    handleEnableBtn,
    handleDeleteZone,
    deviceDetail,
    configStatus,
    selectedZoneData,
    refetch,
    isFetching
  };
};
